import { Check, Gift, Percent, Ticket as TicketIcon, Truck } from 'lucide-react';
import type { Offer, OfferIcon } from '../../lib/offers';
import * as S from './styles';

const ICONS: Record<OfferIcon, typeof Truck> = {
  truck: Truck,
  ticket: TicketIcon,
  percent: Percent,
  gift: Gift,
};

const APPLY_LABEL: Record<Offer['applyMode'], string> = {
  auto: 'تطبيق تلقائي',
  checkout: 'فعّلها عند الدفع',
};

interface OfferCardProps {
  offer: Offer;
  /** يجعل البطاقة قابلة للنقر (زر) */
  onSelect?: (offer: Offer) => void;
  /** إظهار شريط التقدّم — نعرضه حيث توجد سلة فعلية (صفحة الدفع) */
  showProgress?: boolean;
}

export function OfferCard({ offer, onSelect, showProgress }: OfferCardProps) {
  const Icon = ICONS[offer.icon];
  const applied = offer.state === 'applied';
  const interactive = typeof onSelect === 'function';
  const withProgress =
    showProgress === true && offer.state === 'locked' && typeof offer.progress === 'number';

  return (
    <S.Frame>
      <S.TicketStack $tone={offer.tone} $applied={applied}>
        {offer.stacked && (
          <>
            <S.StackLayer $depth={1} aria-hidden="true" />
            <S.StackLayer $depth={2} aria-hidden="true" />
          </>
        )}

        <S.Ticket
          as={interactive ? 'button' : 'div'}
          type={interactive ? 'button' : undefined}
          onClick={interactive ? () => onSelect?.(offer) : undefined}
          $tone={offer.tone}
          $applied={applied}
          $interactive={interactive}
          aria-label={`${offer.title} — ${offer.subtitle}`}
        >
          <S.TicketBody>
            <S.TitleRow>
              <S.TitleIcon $tone={offer.tone} aria-hidden="true">
                <Icon size={14} strokeWidth={2.25} />
              </S.TitleIcon>
              <S.Title $tone={offer.tone}>{offer.title}</S.Title>
            </S.TitleRow>
            <S.Subtitle>{offer.subtitle}</S.Subtitle>

            {withProgress && (
              <S.ProgressTrack aria-hidden="true">
                <S.ProgressFill $tone={offer.tone} $value={offer.progress ?? 0} />
              </S.ProgressTrack>
            )}

            {offer.statusText && (withProgress || applied) && (
              <S.StatusText $tone={offer.tone} $applied={applied}>
                {applied && <Check size={13} strokeWidth={3} aria-hidden="true" />}
                {offer.statusText}
              </S.StatusText>
            )}
          </S.TicketBody>

          {offer.badge && <S.Badge aria-hidden="true">{offer.badge}</S.Badge>}
        </S.Ticket>
      </S.TicketStack>

      <S.FrameFooter>{applied ? 'مُطبَّق على طلبك' : APPLY_LABEL[offer.applyMode]}</S.FrameFooter>
    </S.Frame>
  );
}

export default OfferCard;
